import { Box, Stack, TextField } from '@mui/material';
import type { AccountInput, NewAccount } from '@vy/types';
import { countries } from 'countries-list';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CountrySelect } from '../../../../../../../../components/new-design/inputs/CountrySelect';

export const BankInfoTabContent: React.FC = () => {
  const {
    control,
    register,
    formState: { defaultValues },
  } = useFormContext<AccountInput, unknown, NewAccount>();

  const registerBankName = register('settings.bankDetails.bankName');
  const registerRoutingNumber = register('settings.bankDetails.routingNumber');
  const registerCompanyName = register('settings.bankDetails.companyName');
  const registerAccountNumber = register('settings.bankDetails.accountNumber');
  const registerStreetAddress = register('settings.bankDetails.address.addressText');
  const registerCity = register('settings.bankDetails.address.city');
  const registerState = register('settings.bankDetails.address.state');
  const registerPostalCode = register('settings.bankDetails.address.postalCode');
  const registerCountry = register('settings.bankDetails.address.country');
  const registerSwiftCode = register('settings.bankDetails.swiftCode');
  const registerAbaNumber = register('settings.bankDetails.abaNumber');
  const registerSortCode = register('settings.bankDetails.sortCode');

  return (
    <Stack direction="column" sx={{
      rowGap: "12px"
    }}>
      <Controller
        control={control}
        name={registerBankName.name}
        defaultValue={defaultValues?.settings?.bankDetails?.bankName ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Bank name" {...field} />}
      />
      <Controller
        control={control}
        name={registerAccountNumber.name}
        defaultValue={defaultValues?.settings?.bankDetails?.accountNumber ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Account number" {...field} />}
      />
      <Controller
        control={control}
        name={registerCompanyName.name}
        defaultValue={defaultValues?.settings?.bankDetails?.companyName ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Company name" {...field} />}
      />
      <Controller
        control={control}
        name={registerStreetAddress.name}
        defaultValue={defaultValues?.settings?.bankDetails?.address?.addressText ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Street address" {...field} />}
      />
      <Controller
        control={control}
        name={registerCountry.name}
        defaultValue={defaultValues?.settings?.bankDetails?.address?.country ?? ''}
        render={({ field }) => (
          <CountrySelect
            value={getCountryByName(field.value ?? '')}
            textFieldProps={{
              ...field,
              label: 'Country',
            }}
            onChange={(country) => {
              field.onChange(country?.name);
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={registerCity.name}
        defaultValue={defaultValues?.settings?.bankDetails?.address?.city ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="City" {...field} />}
      />
      <Stack direction="row" sx={{
        justifyContent: "space-between"
      }}>
        <Box
          sx={{
            display: 'flex',
            flex: 8,
            columnGap: '16px',
          }}
        >
          <Controller
            control={control}
            name={registerState.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.state ?? ''}
            render={({ field }) => <TextField size="small" fullWidth label="State" {...field} />}
          />

          <Controller
            control={control}
            name={registerPostalCode.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.postalCode ?? ''}
            render={({ field }) => <TextField size="small" fullWidth label="ZIP / Postal code" {...field} />}
          />
        </Box>
      </Stack>
      <Stack direction="row" sx={{
        justifyContent: "space-between"
      }}>
        <Box
          sx={{
            display: 'flex',
            flex: 8,
            columnGap: '16px',
          }}
        >
          <Controller
            control={control}
            name={registerRoutingNumber.name}
            defaultValue={defaultValues?.settings?.bankDetails?.routingNumber ?? ''}
            render={({ field }) => <TextField size="small" fullWidth label="IBAN (routing)" {...field} />}
          />

          <Controller
            control={control}
            name={registerSwiftCode.name}
            defaultValue={defaultValues?.settings?.bankDetails?.swiftCode ?? ''}
            render={({ field }) => <TextField size="small" fullWidth label="Swift code" {...field} />}
          />
        </Box>
      </Stack>
      <Controller
        control={control}
        name={registerAbaNumber.name}
        defaultValue={defaultValues?.settings?.bankDetails?.abaNumber ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="ABA" {...field} />}
      />
      <Controller
        control={control}
        name={registerSortCode.name}
        defaultValue={defaultValues?.settings?.bankDetails?.sortCode ?? ''}
        render={({ field }) => <TextField size="small" fullWidth label="Sort code" {...field} />}
      />
    </Stack>
  );
};

function getCountryByName(countryName: string) {
  return Object.values(countries).find((country) => country.name === countryName) ?? null;
}
